import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CalendarClock, Plus, Trophy, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { FixtureStats } from "@/components/FixtureStats";
import { usePermissions } from "@/lib/perms";
import { errorText } from "@/components/academy/HubUI";
import { toast } from "sonner";

const TYPES = ["match", "friendly", "trial", "tournament", "tour"];
const blank = () => ({
  type: "match", date: new Date().toISOString().slice(0, 10), time: "", opponent: "", venue: "",
  home_away: "home", competition: "", notes: "", squad: "", sport: "", athlete_ids: [],
});

export default function Fixtures({lockedSquad="",academyId=""}) {
  const [fixtures, setFixtures] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [f, setF] = useState({...blank(),squad:lockedSquad});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [resultFor, setResultFor] = useState(null);
  const { canEdit } = usePermissions();
  const [sports, setSports] = useState([]), [loadError, setLoadError] = useState("");
  useEffect(() => { api.get('/sports/match-stats').then(r=>setSports(r.data.sports || [])).catch(()=>setLoadError('Could not load sport definitions. Retry before recording statistics.')); }, []);

  const load = useCallback(() => {
    setLoadError("");
    api.get(`/fixtures?${new URLSearchParams({squad:lockedSquad,academy_id:academyId})}`).then((r) => setFixtures(r.data.fixtures || [])).catch(e=>setLoadError(errorText(e)));
    api.get("/athletes").then((r) => setAthletes(r.data.filter(a=>(!lockedSquad || (a.teams || [a.sub_team || a.squad || a.sport]).includes(lockedSquad)) && (!academyId || (a.academy_id || '__unaffiliated__')===academyId)))).catch(e=>setLoadError(errorText(e)));
  }, [lockedSquad,academyId]);
  useEffect(() => { load(); }, [load]);

  const squads = Array.from(new Set(athletes.flatMap((a) => a.teams || [a.sub_team || a.squad || a.sport]))).filter(Boolean);
  const pool = f.squad ? athletes.filter((a) => (a.teams || [a.sub_team || a.squad || a.sport]).includes(f.squad)) : athletes;
  const selected = f.athlete_ids;
  const unavailableSelected = pool.filter((a) => selected.includes(a.athlete_id) && a.availability === "unavailable");

  const toggle = (id) => setF((p) => ({ ...p, athlete_ids: p.athlete_ids.includes(id) ? p.athlete_ids.filter((x) => x !== id) : [...p.athlete_ids, id] }));

  const create = async (override = false) => {
    if (!f.date) return toast.error("Pick a date");
    setBusy(true); setConflicts([]);
    try {
      const { data } = await api.post("/fixtures", { ...f, academy_id:academyId, squad:lockedSquad || f.squad, override_unavailable: override });
      setConflicts(data.conflicts || []);
      toast.success(`${f.type.charAt(0).toUpperCase() + f.type.slice(1)} created for ${data.fixture.athlete_ids.length} athlete(s)`);
      setF({...blank(),squad:lockedSquad}); setOpen(false); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.unavailable) {
        if (window.confirm(`${d.unavailable.map((u) => u.name).join(", ")} ${d.unavailable.length === 1 ? "is" : "are"} marked unavailable. Include them anyway?`)) {
          setBusy(false); return create(true);
        }
      } else toast.error(typeof d === "string" ? d : "Could not create the fixture");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this fixture?")) return;
    try { await api.delete(`/fixtures/${id}`); toast.success("Fixture deleted; linked player stats and match work removed"); load(); }
    catch(e) { toast.error(errorText(e)); }
  };

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center py-3 gap-3 px-4 sm:px-8 sticky top-0 bg-background z-10">
        <CalendarClock className="w-5 h-5" />
        <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">Matches & Fixtures</h1>
        {canEdit('matches') && <button data-testid="new-fixture-btn" onClick={() => setOpen((v) => !v)}
          className="ml-auto text-sm px-3 py-2 bg-foreground text-background flex items-center gap-1.5 hover:bg-neutral-800 transition-colors">
          <Plus className="w-4 h-4" /> New
        </button>}
      </header>
      <p data-testid="fixtures-stats-intro" className="text-sm text-muted-foreground px-4 sm:px-8 py-4">Open Match stats to record the sport’s scorecard once. Each selected player’s contribution appears automatically in their own Match Stats history.</p>
      {loadError && <div role="alert" data-testid="fixtures-load-error" className="px-4 sm:px-8 pb-4 text-sm text-traffic-red">{loadError} <button data-testid="fixtures-retry" className="underline" onClick={load}>Retry</button></div>}

      {open && (
        <div className="border-b border-border p-4 sm:p-8" data-testid="fixture-form">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Type</span>
              <select data-testid="fixture-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="inp mt-1">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Sport / scorecard</span><select data-testid="fixture-sport" className="inp mt-1" value={f.sport} onChange={e=>setF({...f,sport:e.target.value})}><option value="">Use participants’ sport</option>{sports.map(s=><option value={s.id} key={s.id}>{s.name}</option>)}</select></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Date</span>
              <input data-testid="fixture-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="inp mt-1" /></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Time</span>
              <input data-testid="fixture-time" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="inp mt-1" /></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Opponent</span>
              <input data-testid="fixture-opponent" value={f.opponent} onChange={(e) => setF({ ...f, opponent: e.target.value })} className="inp mt-1" /></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Venue</span>
              <input data-testid="fixture-venue" value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} className="inp mt-1" /></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Home / away</span>
              <select data-testid="fixture-homeaway" value={f.home_away} onChange={(e) => setF({ ...f, home_away: e.target.value })} className="inp mt-1">
                <option value="home">Home</option><option value="away">Away</option><option value="neutral">Neutral</option>
              </select></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Competition</span>
              <input data-testid="fixture-competition" value={f.competition} onChange={(e) => setF({ ...f, competition: e.target.value })} className="inp mt-1" /></label>
            <label><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Squad</span>
              <select data-testid="fixture-squad" disabled={!!lockedSquad} value={f.squad} onChange={(e) => setF({ ...f, squad: e.target.value, athlete_ids: [] })} className="inp mt-1">
                <option value="">All athletes</option>
                {squads.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></label>
          </div>
          <label className="block mt-3"><span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Notes</span>
            <input data-testid="fixture-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="inp mt-1" /></label>

          <div className="mt-5">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="text-xs tracking-[0.18em] uppercase font-bold">Matchday squad</span>
              <span className="text-xs text-muted-foreground" data-testid="selection-counts">
                {selected.length} selected · {pool.length - selected.length} unselected · {pool.filter((a) => a.availability === "unavailable").length} unavailable
              </span>
              <button data-testid="select-all-athletes" onClick={() => setF({ ...f, athlete_ids: pool.filter((a) => a.availability !== "unavailable").map((a) => a.athlete_id) })}
                className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">Select available</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
              {pool.map((a) => (
                <label key={a.athlete_id} data-testid={`fixture-athlete-${a.athlete_id}`}
                  className={`bg-background p-2.5 flex items-center gap-2 text-sm cursor-pointer hover:bg-surface transition-colors ${a.availability === "unavailable" ? "opacity-60" : ""}`}>
                  <input data-testid={`fixture-select-${a.athlete_id}`} type="checkbox" checked={selected.includes(a.athlete_id)} onChange={() => toggle(a.athlete_id)} />
                  <span className="flex-1 truncate">{a.name}</span>
                  {a.availability !== "available" && <span className="text-[10px] uppercase tracking-wider text-traffic-amber">{a.availability}</span>}
                  {(a.injury_risk ?? 0) >= 65 && <AlertTriangle className="w-3.5 h-3.5 text-traffic-red" title="High injury risk" />}
                </label>
              ))}
            </div>
            {unavailableSelected.length > 0 && (
              <p className="text-xs text-traffic-amber mt-2" data-testid="unavailable-warning">
                {unavailableSelected.map((a) => a.name).join(", ")} {unavailableSelected.length === 1 ? "is" : "are"} marked unavailable · you will be asked to confirm an override.
              </p>
            )}
          </div>

          <div className="flex gap-3 mt-5">
            <button data-testid="fixture-create" onClick={() => create(false)} disabled={busy}
              className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-1.5">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create fixture
            </button>
            <button data-testid="fixture-create-cancel" onClick={() => { setOpen(false); setF({...blank(),squad:lockedSquad}); }} className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="border-b border-border p-4 sm:p-8" data-testid="fixture-conflicts">
          <div className="text-xs tracking-[0.18em] uppercase font-bold text-traffic-amber flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Training clashes on that date</div>
          <div className="space-y-1.5 text-sm">
            {conflicts.map((c) => (
              <div key={c.athlete_id} className="text-muted-foreground">
                <b className="text-foreground">{c.name}</b> · {c.tasks.map((t) => t.title).join(", ")}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Move or drop these in the athlete's calendar, or use Command Center bulk actions to shift the whole squad.</p>
        </div>
      )}

      <div className="scroll-x">
        <table className="w-full text-sm" data-testid="fixtures-table">
          <thead>
            <tr className="border-b border-border bg-surface text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Opponent</th>
              <th className="text-left p-3">Venue</th><th className="text-left p-3">Squad</th><th className="text-left p-3">Selected</th>
              <th className="text-left p-3">Result</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {fixtures.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground" data-testid="fixtures-empty">
                No fixtures yet. Create a match, friendly, trial, tournament or tour and pick the matchday squad.
              </td></tr>
            ) : fixtures.map((x) => (
              <tr key={x.fixture_id} className="border-b border-border" data-testid={`fixture-row-${x.fixture_id}`}>
                <td className="p-3 tabular-nums whitespace-nowrap">{fmtDate(x.date)}{x.time ? ` · ${x.time}` : ""}</td>
                <td className="p-3 capitalize">{x.type}</td>
                <td className="p-3">{x.opponent || "-"}</td>
                <td className="p-3 text-muted-foreground">{x.venue || "-"} {x.home_away ? `(${x.home_away})` : ""}</td>
                <td className="p-3 text-muted-foreground">{x.squad || "All"}</td>
                <td className="p-3 tabular-nums">{(x.athlete_ids || []).length}</td>
                <td className="p-3" data-testid={`fixture-summary-${x.fixture_id}`}><p>{x.result ? `${x.result} ${x.score || ""}` : "Result not recorded"}</p><p className="text-xs text-muted-foreground mt-1">{x.sport || "Sport not set"} · {(x.participation || []).filter(p=>Object.entries(p).some(([k,v])=>!["athlete_id","participation_status"].includes(k) && v !== null && v !== "")).length}/{x.athlete_ids?.length || 0} player scorecards</p>{Object.entries(x.team_stats || {}).slice(0,3).map(([key,value])=><p key={key} data-testid={`fixture-team-stat-${x.fixture_id}-${key}`} className="text-xs mt-1">{key.replaceAll("_"," ")}: {value}</p>)}</td>
                <td className="p-3">
                  <span className="flex items-center gap-2 justify-end">
                    <button data-testid={`fixture-result-${x.fixture_id}`} onClick={() => setResultFor(x.fixture_id)}
                      className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors flex items-center gap-1"><Trophy className="w-3 h-3" /> Match stats</button>
                    {canEdit('matches') && <button data-testid={`fixture-del-${x.fixture_id}`} aria-label="Delete fixture" onClick={() => del(x.fixture_id)} className="text-muted-foreground hover:text-traffic-red"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resultFor && <FixtureStats fixtureId={resultFor} onClose={()=>setResultFor(null)} onSaved={load}/>}
    </div>
  );
}
